import React, { useState, ChangeEvent, KeyboardEvent } from 'react';
import styles from './FieldCreationBar.module.css';
import layout from '../../../../Modules/layout.module.css';
import effects from '../../../../Modules/effects.module.css';
import DropDownList from '../../DropDownList/DropDownList';
import { IDropDownListItem } from '../../../../Types/DropDownList/IDropDownListItem';
import { AdditionalInfoFieldType } from '../../../../Types/AdditionalInformation/AdditionalInfoField';

type FieldCreationBarProps = {
  onCreateHandler: (fieldType: AdditionalInfoFieldType, title: string, options?: string[]) => void;
  placeholder?: string;
  noTextMessage?: string;
};

const FieldCreationBar = ({
  onCreateHandler,
  placeholder = 'Enter field title...',
  noTextMessage = 'Field title is required'
}: FieldCreationBarProps) => {
  const [inputValue, setInputValue] = useState<string>('');
  const [selectedFieldType, setSelectedFieldType] = useState<AdditionalInfoFieldType>(AdditionalInfoFieldType.TextArea);
  const [warningMessage, setWarningMessage] = useState<string>('');
  const [animate, setAnimate] = useState<boolean>(false);
  const [showOptionsModal, setShowOptionsModal] = useState<boolean>(false);
  const [options, setOptions] = useState<string[]>([]);
  const [currentOption, setCurrentOption] = useState<string>('');

  const fieldTypeOptions: IDropDownListItem[] = [
    { value: AdditionalInfoFieldType.TextArea.toString(), text: 'Text Area', colour: undefined, textColour: undefined },
    { value: AdditionalInfoFieldType.DatePicker.toString(), text: 'Date Picker', colour: undefined, textColour: undefined },
    { value: AdditionalInfoFieldType.TimePicker.toString(), text: 'Time Picker', colour: undefined, textColour: undefined },
    { value: AdditionalInfoFieldType.DropDownList.toString(), text: 'Dropdown List', colour: undefined, textColour: undefined },
    { value: AdditionalInfoFieldType.MultiSelect.toString(), text: 'Multi Select', colour: undefined, textColour: undefined },
    { value: AdditionalInfoFieldType.Switch.toString(), text: 'Switch', colour: undefined, textColour: undefined }
  ];

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
  };

  const handleFieldTypeChange = (selectedItem: IDropDownListItem) => {
    setSelectedFieldType(parseInt(selectedItem.value) as AdditionalInfoFieldType);
  };

  const handleCreateClick = () => {
    if (inputValue.trim() === '') {
      setWarningMessage(noTextMessage);
      setAnimate(true);
      setTimeout(() => setAnimate(false), 500);
    } else {
      setWarningMessage('');
      
      // If dropdown or multiselect, show options modal
      if (selectedFieldType === AdditionalInfoFieldType.DropDownList || selectedFieldType === AdditionalInfoFieldType.MultiSelect) {
        setShowOptionsModal(true);
      } else {
        // Create field without options
        onCreateHandler(selectedFieldType, inputValue);
        setInputValue('');
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleCreateClick();
    }
  };

  const handleAddOption = () => {
    if (currentOption.trim() !== '') {
      setOptions([...options, currentOption.trim()]);
      setCurrentOption('');
    }
  };

  const handleRemoveOption = (index: number) => {
    setOptions(options.filter((_, i) => i !== index));
  };

  const handleConfirmOptions = () => {
    if (options.length > 0) {
      onCreateHandler(selectedFieldType, inputValue, options);
      setInputValue('');
      setOptions([]);
      setCurrentOption('');
      setShowOptionsModal(false);
    }
  };

  const handleCancelOptions = () => {
    setOptions([]);
    setCurrentOption('');
    setShowOptionsModal(false);
  };

  return (
    <>
      <div className={`${layout.row} ${effects.shadow} ${styles.creationBarWrapper}`}>
        <button className={styles.createButton} onClick={handleCreateClick}>
          <img src='/img/archive.svg' alt='Add Field' />
          <p className={styles.createButtonText}>Add Field</p>
        </button>
        <div className={styles.fieldTypeContainer}>
          <DropDownList
            value={selectedFieldType.toString()}
            placeholder="Select field type"
            data={fieldTypeOptions}
            onChange={handleFieldTypeChange}
          />
        </div>
        <input
          className={`${styles.createInput} ${animate ? `${styles.shake} ${styles.pulse}` : ''}`}
          placeholder={placeholder}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
        />
        {warningMessage && <p className={styles.warningMessage}>{warningMessage}</p>}
      </div>

      {showOptionsModal && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <h3>Add Options for {inputValue}</h3>
            <p className={styles.modalDescription}>
              Add options for your {selectedFieldType}. Users will choose from these options.
            </p>

            <div className={styles.optionInputWrapper}>
              <input
                className={styles.optionInput}
                placeholder="Enter option..."
                value={currentOption}
                onChange={(e) => setCurrentOption(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddOption()}
              />
              <button className={styles.addOptionButton} onClick={handleAddOption}>
                Add Option
              </button>
            </div>

            {options.length > 0 && (
              <div className={styles.optionsList}>
                <p className={styles.optionsLabel}>Options ({options.length}):</p>
                {options.map((option, index) => (
                  <div key={index} className={styles.optionItem}>
                    <span>{option}</span>
                    <button
                      className={styles.removeOptionButton}
                      onClick={() => handleRemoveOption(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className={styles.modalActions}>
              <button className={styles.cancelButton} onClick={handleCancelOptions}>
                Cancel
              </button>
              <button
                className={styles.confirmButton}
                onClick={handleConfirmOptions}
                disabled={options.length === 0}
              >
                Create Field
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FieldCreationBar;


